import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useState } from "react";
import { Coins } from "lucide-react";
import { useBillingGate } from "@/lib/twa";
import { AppBillingNotice } from "@/components/AppBillingNotice";

const PACKAGES = [
  { key: "starter", coins: 100, price: 0.99 },
  { key: "popular", coins: 550, price: 4.99, tag: "Popular" },
  { key: "value", coins: 1200, price: 9.99, tag: "Best value" },
  { key: "whale", coins: 3200, price: 24.99 },
];

export const BuyCoinsDialog = ({ open, onOpenChange }) => {
  const { setCoins } = useAuth();
  const [pkg, setPkg] = useState("popular");
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const { gated } = useBillingGate();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-[#0C0C14] text-white" data-testid="buycoins-dialog">
        {gated ? (
          <AppBillingNotice onBack={() => onOpenChange(false)} />
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
            <Coins className="text-[#FFD600]" /> Get Coins
          </DialogTitle>
          <DialogDescription className="text-zinc-400">Coins power gifts. Send love to your favorite creators.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2" data-testid="coin-packages">
          {PACKAGES.map((p) => (
            <button key={p.key} onClick={() => setPkg(p.key)} data-testid={`coin-pkg-${p.key}`}
              className={`relative rounded-xl border p-3 text-left transition-colors ${
                pkg === p.key ? "border-[#FFD600] bg-[#FFD600]/10" : "border-white/10"
              }`}>
              {p.tag && <span className="absolute -top-2 right-2 rounded-full bg-[#FF0055] px-2 py-0.5 text-[9px] font-bold uppercase text-white">{p.tag}</span>}
              <div className="flex items-center gap-1.5 font-display text-lg font-extrabold text-[#FFD600]">
                <Coins size={16} /> {p.coins.toLocaleString()}
              </div>
              <div className="font-mono text-sm text-zinc-300">${p.price}</div>
            </button>
          ))}
        </div>

        {clientId ? (
          <div data-testid="coins-paypal-buttons">
            <PayPalScriptProvider options={{ clientId, currency: "USD", intent: "capture" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
                forceReRender={[pkg]}
                createOrder={async () => {
                  try { const { data } = await api.post("/paypal/create-coins", { package: pkg }); return data.id; }
                  catch (e) { toast.error(apiError(e.response?.data?.detail)); throw e; }
                }}
                onApprove={async (data) => {
                  try {
                    const { data: res } = await api.post(`/paypal/capture-coins/${data.orderID}`);
                    setCoins(res.coins);
                    toast.success(`+${res.added} coins added! 🪙`);
                    onOpenChange(false);
                  } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Purchase failed"); }
                }}
                onError={() => toast.error("PayPal error — please try again")}
              />
            </PayPalScriptProvider>
          </div>
        ) : (
          <p className="text-center text-sm text-[#FF0055]">PayPal is not configured.</p>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
